/**
 * 버튼·강조 색 팔레트 — 운영자가 **고르는** 것이지 **만드는** 것이 아니다.
 *
 * ## 왜 자유 색상 선택이 아닌가
 * 색은 예뻐 보이는지로 고르게 되는데, 그러면 대비가 깨진다. 실제로 이 프로젝트도
 * 그랬다 — 초록 버튼 글자가 3.35:1, 버튼 테두리가 배경과 1.28:1 이라 밝은 야외에서
 * 아이가 버튼을 못 찾는 상태였다(2026-07-28 실측·수정).
 *
 * 그래서 색상환을 주지 않는다. **미리 대비를 계산해 통과시킨 팔레트만** 고르게 한다.
 * `palette.test.ts` 가 모든 팔레트를 검사하므로, 새 팔레트가 기준을 못 넘기면
 * 테스트가 막는다 — 사람 눈이 아니라 숫자가 문지기다.
 *
 * ## 무엇을 바꾸는가
 * 버튼·링크 계열 색만 바꾼다. 배경(양피지)·본문 잉크·둥근 정도는 안 바꾼다 —
 * 그건 브랜드 정체성이고, 바꾸면 그림책 톤이 통째로 무너진다.
 */

export interface Palette {
  id: string;
  /** 운영자에게 보여줄 이름. */
  label: string;
  hint: string;
  /** 주 동작(초록 자리) 계열. */
  brand: string;
  brandLit: string;
  brandDeep: string;
  brandEdge: string;
  brandInk: string;
  /** 보상·구매(골드 자리) 계열. */
  ctaFrom: string;
  ctaTo: string;
  ctaEdge: string;
  ctaInk: string;
}

export const PALETTES: readonly Palette[] = [
  {
    id: "forest",
    label: "숲 그린",
    hint: "기본 — 경주 밤 숲과 달빛 금색",
    brand: "#2c7148",
    brandLit: "#36804f",
    brandDeep: "#22603c",
    brandEdge: "#1b4f31",
    brandInk: "#f5fbf7",
    ctaFrom: "#f8cd80",
    ctaTo: "#dda247",
    ctaEdge: "#b07d2b",
    ctaInk: "#4a3410",
  },
  {
    id: "night",
    label: "달빛 남색",
    hint: "차분한 밤하늘 — 별·달 이야기에 어울려요",
    brand: "#2f5d8a",
    brandLit: "#3a6d9e",
    brandDeep: "#254a70",
    brandEdge: "#1c3a58",
    brandInk: "#f4f9ff",
    ctaFrom: "#f6d089",
    ctaTo: "#e0a94a",
    ctaEdge: "#b07d2b",
    ctaInk: "#40320e",
  },
  {
    id: "sunset",
    label: "노을 주황",
    hint: "따뜻한 저녁빛 — 가을 행사에 어울려요",
    brand: "#a2482f",
    brandLit: "#b25439",
    brandDeep: "#8a3a24",
    brandEdge: "#6d2c1a",
    brandInk: "#fff6f1",
    ctaFrom: "#f7c07a",
    ctaTo: "#e09a4c",
    ctaEdge: "#a86f27",
    ctaInk: "#46300f",
  },
];

export const DEFAULT_PALETTE_ID = "forest";

/** 모르는 id 면 기본 팔레트 — 저장값이 손상돼도 화면은 정상이다(불변식 3·7). */
export function paletteById(id: string | undefined | null): Palette {
  return PALETTES.find((p) => p.id === id) ?? PALETTES[0]!;
}

export function paletteIds(): string[] {
  return PALETTES.map((p) => p.id);
}

/**
 * 팔레트 → CSS 변수. base.css 의 토큰 이름과 **정확히 같아야** 한다 —
 * 이름이 어긋나면 조용히 아무것도 안 바뀐다(테스트가 이 대응을 확인한다).
 */
export function paletteCssVars(p: Palette): Record<string, string> {
  return {
    "--brand": p.brand,
    "--brand-lit": p.brandLit,
    "--brand-deep": p.brandDeep,
    "--brand-edge": p.brandEdge,
    "--brand-ink": p.brandInk,
    "--cta-from": p.ctaFrom,
    "--cta-to": p.ctaTo,
    "--cta-edge": p.ctaEdge,
    "--cta-ink": p.ctaInk,
  };
}
